import { getDigitalStore } from "@/store/digital";
import audioManager from "@/utils/audioManager";
import { useEffect, useState, useRef } from "react";

/**
 * 监测系统活跃状态的Hook
 * @param idleTimeout 空闲超时时间，默认10秒
 * @param props 监听的属性对象，当这些属性变化时会重新计时
 * @param callback 状态变化回调
 * @returns 当前是否空闲
 */
export function useActivityStatus(
  idleTimeout: number = 10000, 
  props: Record<string, any> = {}, 
  callback: (isIdle: boolean) => void
) {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 设置活跃状态
  const setActive = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    if (isIdle) {
      setIsIdle(false);
      callback(false);
      getDigitalStore().update((state) => {
        state.isIdle = false;
      });
    }
  };

  // 开始空闲倒计时
  const startIdleTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    timerRef.current = setTimeout(() => {
      setIsIdle(true);
      callback(true);
      getDigitalStore().update((state) => {
        state.isIdle = true;
      });
    }, idleTimeout);
  };

  useEffect(() => {
    // 检查是否应该保持活跃状态
    const shouldBeActive = () => {
      const isAudioPlaying = audioManager.getState() === 'playing';
      const hasActiveProps = Object.values(props).some(value => Boolean(value));
      return isAudioPlaying || hasActiveProps;
    };

    if (shouldBeActive()) {
      // 有活跃条件，设置为活跃状态
      setActive();
    } else {
      // 没有活跃条件，开始空闲倒计时
      startIdleTimer();
    }

    // 定期检查音频状态
    const interval = setInterval(() => {
      if (shouldBeActive()) {
        setActive();
      } else if (!timerRef.current) {
        // 如果当前没有计时器且不应该活跃，开始倒计时
        startIdleTimer();
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [idleTimeout, ...Object.values(props)]);

  return isIdle;
}
